/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  AfterViewInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnInit,
} from '@angular/core';
import {
  MessageFromActor,
  MessageItemContent,
  MessageList,
  Referral,
} from '@interfaces';
import { IInfiniteScrollEvent } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'unicon-list-message-item',
  templateUrl: './list-message-item.component.html',
  styleUrl: './list-message-item.component.scss',
})
export class ListMessageItemComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChild('chatListContainer') container!: ElementRef<HTMLDivElement>;
  @ViewChild('chatContainer') chatContainer!: ElementRef<HTMLDivElement>;
  @Input() messages!: MessageFromActor[];
  @Input() referral!: Referral;
  @Input() pageNumber!: number;
  @Input() lastPage!: number;
  @Input() topHeight!: number;
  @Input() heightContainer!: number;
  @Output() scrollUp: EventEmitter<IInfiniteScrollEvent> =
    new EventEmitter<IInfiniteScrollEvent>();
  @Output() image: EventEmitter<string> = new EventEmitter<string>();
  @Input() isTabViewChat!: boolean;

  protected previousScrollHeight: number = 0;
  protected isLoadingMoreMessages = false;
  protected lastMessagesLength: number = 0;
  protected lastScrollTop: number = 0;
  protected isUserAtBottom = true;
  protected isFirstLoad = true;
  protected newMessageList: MessageList[] = [];
  private scrollBottomSubject = new Subject<void>();
  protected showScrollButton = false;

  ngOnInit(): void {
    this.planeMessages();
    this.scrollBottomSubject.pipe(debounceTime(100)).subscribe(() => {
      if (!this.container || !this.container.nativeElement) {
        return;
      }
      const div: HTMLDivElement = this.container.nativeElement;
      div.scrollTo({
        top: div.scrollHeight,
        behavior: 'auto',
      });
    });
  }

  ngAfterViewInit(): void {
    // Solo hacer scroll al fondo en la primera carga, cuando el ViewChild ya está disponible
    if (this.isFirstLoad && this.messages?.length) {
      this.isFirstLoad = false;
      this.scrollBottom();
      this.lastMessagesLength = this.newMessageList.length;
      this.previousScrollHeight = this.container.nativeElement.scrollHeight;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.container?.nativeElement) return;

    if (changes['messages']) {
      this.planeMessages();
      this.handleMessageChanges();
    }
  }

  protected handleMessageChanges(): void {
    const element = this.container.nativeElement;

    const hasNewMessages =
      this.newMessageList?.length !== this.lastMessagesLength;
    this.updateScrollButtonVisibility(hasNewMessages);

    if (hasNewMessages) {
      if (this.isUserAtBottom) {
        this.scrollBottom();
      } else if (this.isLoadingMoreMessages) {
        this.restorePreviousScrollPosition(element);
      }

      this.updateScrollTracking(element);
    }
  }

  protected updateScrollButtonVisibility(hasNewMessages: boolean): void {
    this.showScrollButton = !this.isUserAtBottom && hasNewMessages;
  }

  protected restorePreviousScrollPosition(element: HTMLElement): void {
    requestAnimationFrame(() => {
      const newScrollHeight = element.scrollHeight;
      element.scrollTop = newScrollHeight - this.previousScrollHeight;
      this.isLoadingMoreMessages = false;
    });
  }

  protected updateScrollTracking(element: HTMLElement): void {
    this.lastMessagesLength = this.newMessageList.length;
    this.previousScrollHeight = element.scrollHeight;
  }

  private planeMessages() {
    this.newMessageList = [];
    if (this.messages && this.messages.length > 0) {
      for (const msg of this.messages) {
        for (let i = 0; i < msg.items.length; i++) {
          const item = msg.items[i];
          this.newMessageList.push({
            fromName: msg.fromName,
            from: msg.from,
            item: item,
            showName: i === 0,
          });
        }
      }
    }
  }

  protected onScroll(): void {
    const element = this.container.nativeElement;
    const isAtTop = element.scrollTop === 0;

    // Calcular si está al fondo sin variable redundante
    this.isUserAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight,
      ) < 2;

    if (isAtTop && !this.isLoadingMoreMessages) {
      this.previousScrollHeight = element.scrollHeight;
      this.isLoadingMoreMessages = true;
      this.scrollUp.emit();
    }
    if (
      element.scrollHeight - element.clientHeight - 0.5 ===
      element.scrollTop
    ) {
      this.showScrollButton = false;
    }
    this.lastScrollTop = element.scrollTop;
  }

  protected scrollBottom(): void {
    this.scrollBottomSubject.next();
    if (!this.isUserAtBottom) {
      this.showScrollButton = false;
    }
  }

  protected openGoogleMaps(data: MessageItemContent): void {
    const url = `https://www.google.com/maps?q=${data?.location?.latitude},${data?.location?.longitude}`;
    window.open(url, '_blank');
  }

  protected contentText() {
    if (this.referral) {
      if (this.referral.sourceType === 'ad') {
        return 'publicidad';
      }
      if (this.referral.sourceType === 'post') {
        return 'publicación';
      }
      return '';
    }
    return '';
  }

  // trackBy para optimizar el renderizado de mensajes
  protected trackByMessage(index: number, message: MessageList): any {
    return message.item?.id || index;
  }
}
